import React from 'react';
import styled from 'styled-components';

import { theme } from '../../theme';

// ============ TYPES ============

export interface TimelineItemData {
  id: string;
  title: string;
  subtitle?: string;
  period: string;
  badges?: string[];
  description?: string;
  metadata?: string;
  onClick?: () => void;
  actionHint?: string;
}

interface TimelineProps {
  items: TimelineItemData[];
  renderItem?: (item: TimelineItemData) => React.ReactNode;
}

interface TimelineItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  isClickable?: boolean;
}

// ============ STYLED COMPONENTS ============

const TimelineContainer = styled.div`
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${theme.colors.green},
      rgba(31, 207, 94, 0.3)
    );
  }
`;

const TimelineItemWrapper = styled.div<{ $isClickable: boolean }>`
  position: relative;
  padding: 15px 15px 15px 25px;
  margin-bottom: 10px;
  cursor: ${props => (props.$isClickable ? 'pointer' : 'default')};
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: -17px;
    top: 22px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.colors.green};
    border: 2px solid ${theme.colors.background};
    box-shadow: 0 0 8px rgba(31, 207, 94, 0.5);
    z-index: 1;
  }

  ${props =>
    props.$isClickable &&
    `
    &:hover {
      background: rgba(31, 207, 94, 0.05);
      border-color: rgba(31, 207, 94, 0.2);
    }
  `}

  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 8px;
`;

const ItemTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemPeriod = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 12px;
  font-family: 'Courier New', monospace;
  white-space: nowrap;
  background: rgba(31, 207, 94, 0.1);
  padding: 3px 8px;
  border-radius: 4px;
`;

const ItemSubtitle = styled.p`
  margin: 0 0 10px 0;
  color: ${theme.colors.textMuted};
  font-size: 14px;
  font-style: italic;
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const Badge = styled.span`
  background: rgba(31, 207, 94, 0.1);
  color: ${theme.colors.green};
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-family: 'Courier New', monospace;
`;

const ItemDescription = styled.p`
  margin: 10px 0 0 0;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  font-size: 14px;
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const Metadata = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 12px;
`;

const ActionHint = styled.span`
  color: ${theme.colors.green};
  font-size: 12px;
  opacity: 0.7;
`;

// ============ COMPONENTS ============

// Low-level TimelineItem for custom content
export const TimelineItem: React.FC<TimelineItemProps> = ({
  children,
  onClick,
  isClickable = false,
}) => {
  return (
    <TimelineItemWrapper $isClickable={isClickable || !!onClick} onClick={onClick}>
      {children}
    </TimelineItemWrapper>
  );
};

// High-level Timeline with default rendering
const Timeline: React.FC<TimelineProps> = ({ items, renderItem }) => {
  const defaultRender = (item: TimelineItemData) => (
    <TimelineItem
      key={item.id}
      onClick={item.onClick}
      isClickable={!!item.onClick}
    >
      <ItemHeader>
        <ItemTitle>{item.title}</ItemTitle>
        <ItemPeriod>{item.period}</ItemPeriod>
      </ItemHeader>

      {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}

      {item.badges && item.badges.length > 0 && (
        <BadgeContainer>
          {item.badges.map((badge, idx) => (
            <Badge key={idx}>{badge}</Badge>
          ))}
        </BadgeContainer>
      )}

      {item.description && <ItemDescription>{item.description}</ItemDescription>}

      {(item.metadata || item.actionHint) && (
        <ItemFooter>
          {item.metadata && <Metadata>{item.metadata}</Metadata>}
          {item.actionHint && <ActionHint>{item.actionHint}</ActionHint>}
        </ItemFooter>
      )}
    </TimelineItem>
  );

  return (
    <TimelineContainer>
      {items.map(item => (renderItem ? renderItem(item) : defaultRender(item)))}
    </TimelineContainer>
  );
};

// Export styled components for custom compositions
export {
  TimelineContainer,
  TimelineItemWrapper,
  ItemHeader,
  ItemTitle,
  ItemPeriod,
  ItemSubtitle,
  BadgeContainer,
  Badge,
  ItemDescription,
  ItemFooter,
  Metadata,
  ActionHint,
};

export default Timeline;
